/**
 * Text farbig hinterlegen im E-Mail-Editor (Kundenfeedback Kanzlei Polz
 * 24.10.2026, IV.8: „Wenn man bei den Mails in KARS den Text farbig
 * hinterlegen möchte, geht das nicht").
 *
 * Ursache: Der Knopf „Hervorheben" war ein nativer Farbwähler
 * (<input type="color">) mit Vorgabe Gelb.
 *   1. Ein Klick darauf nimmt dem Editor den Fokus; je nach Browser ist die
 *      markierte Textstelle danach weg, und execCommand wirkt ins Leere.
 *   2. Das input-Ereignis feuert nur bei einer Farbänderung – wer die
 *      vorbelegte Farbe Gelb wählte, löste gar nichts aus.
 *   3. hiliteColor setzt je nach Browser nur mit styleWithCSS eine
 *      Hintergrundfarbe.
 * Jetzt: feste Farbfelder (wie Outlook), die den Fokus nicht abgeben, die
 * Markierung vor dem Anwenden wiederherstellen und eine Ersatzlösung für
 * Browser ohne hiliteColor/backColor. Die Farbe landet als
 * style="background-color: …" im HTML und bleibt so auch im Versand-HTML
 * erhalten (der Versand übernimmt den Editor-Inhalt unverändert).
 */
import { gtKey } from '@/i18n/gt'

export interface EditorColor {
  value: string
  label: string
}

/** Hervorhebungsfarben (Texthintergrund) – Auswahl wie in Outlook. */
export const HIGHLIGHT_COLORS: readonly EditorColor[] = [
  { value: '#ffff00', label: gtKey('Gelb') },
  { value: '#00ffff', label: gtKey('Türkis') },
  { value: '#00ff00', label: gtKey('Hellgrün') },
  { value: '#ff00ff', label: gtKey('Pink') },
  { value: '#ffc000', label: gtKey('Orange') },
  { value: '#d9d9d9', label: gtKey('Grau') },
]

/** Schriftfarben für den Text selbst. */
export const TEXT_COLORS: readonly EditorColor[] = [
  { value: '#000000', label: gtKey('Schwarz') },
  { value: '#c00000', label: gtKey('Dunkelrot') },
  { value: '#ff0000', label: gtKey('Rot') },
  { value: '#0070c0', label: gtKey('Blau') },
  { value: '#00b050', label: gtKey('Grün') },
  { value: '#7f7f7f', label: gtKey('Grau') },
]

/** „Keine Hervorhebung" entfernt die Hintergrundfarbe wieder. */
export const NO_HIGHLIGHT = 'transparent'

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

/** Nur echte Farbwerte gelangen in execCommand bzw. das style-Attribut. */
export function isSafeEditorColor (value: unknown): value is string {
  return typeof value === 'string' && (value === NO_HIGHLIGHT || HEX_COLOR.test(value.trim()))
}

/**
 * Markierung als Hintergrund einfärben. Erwartet, dass die Auswahl des
 * Dokuments bereits auf range gesetzt ist. Rückgabe: ob etwas angewendet
 * wurde.
 */
export function applyBackgroundColor (doc: Document, range: Range | null, color: string): boolean {
  if (!isSafeEditorColor(color)) {
    return false
  }
  let applied = false
  try {
    doc.execCommand('styleWithCSS', false, 'true')
    applied = doc.execCommand('hiliteColor', false, color)
    if (!applied) {
      applied = doc.execCommand('backColor', false, color)
    }
  } catch {
    applied = false
  } finally {
    // Andere Befehle (Schriftfarbe, -größe) behalten ihr bisheriges Markup.
    try {
      doc.execCommand('styleWithCSS', false, 'false')
    } catch { /* nicht unterstützt */ }
  }
  if (applied || !range || range.collapsed) {
    return applied
  }
  return wrapRangeInBackground(doc, range, color)
}

/** Ersatzlösung ohne execCommand: Markierung in ein <span> mit Hintergrund packen. */
function wrapRangeInBackground (doc: Document, range: Range, color: string): boolean {
  try {
    const span = doc.createElement('span')
    span.style.backgroundColor = color
    span.append(range.extractContents())
    range.insertNode(span)
    return true
  } catch {
    return false
  }
}
